/* eslint-disable max-len */
import React from 'react'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '../../../../../../common/Modal'
import { ModalHeader } from '../../../../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../../../../hooks/useAppTranslation'
import { ToastType } from '../../../../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../../../../hooks/useToastNotification'
import { societyWebsiteFAQQuestionCreateSchema } from '../../../../../../../forms/schemas/society_website_faq_question_create'
import { CreateUpdateSocietyWebsiteFAQItemForm } from '../CreateUpdateSocietyWebsiteFAQItemForm'
import { NSocietiesWebsites } from '../../../../../../../interfaces/services/societies-websites.interfaces'
import { ISocietyWebsiteModel } from '../../../../../../../interfaces/models/societies-websites.interfaces'
import { patchWebsite } from '../../../../../../../api/society-websites'

interface CreateUpdateSocietyWebsiteFAQItemModalProps {
  website: ISocietyWebsiteModel
  show: boolean
  close: () => void
  children?: React.ReactNode
  questionId?: string
  sectionId?: string
}

export const CreateUpdateSocietyWebsiteFAQItemModal = ({
  website,
  questionId,
  sectionId,
  children,
  show,
  close,
}: CreateUpdateSocietyWebsiteFAQItemModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!questionId

  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateQuestionsAndAnswers.flashMessage.updateQuestionFailure'
      : 'createUpdateQuestionsAndAnswers.flashMessage.createQuestionFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateQuestionsAndAnswers.flashMessage.updateQuestionSuccess'
      : 'createUpdateQuestionsAndAnswers.flashMessage.createQuestionSuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }

  const { mutate: mutatePatchWebsite, isPending: isUpdatingWebsite } =
    useMutation({
      mutationFn: patchWebsite,
    })
  const onSubmit = async (
    data: InferType<typeof societyWebsiteFAQQuestionCreateSchema>
  ): Promise<void> => {
    if (updateMode) {
      const formData: NSocietiesWebsites.NPatch.IRequestBody = {
        brokerQuestion: {
          ...website.brokerQuestion,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          sections: website.brokerQuestion?.sections?.map((_section) => ({
            ..._section,
            questions: _section.questions?.map((_question) => {
              if (_question._id === questionId) {
                return {
                  ..._question,
                  ...data,
                }
              }
              return _question
            }),
          })),
        },
      }
      mutatePatchWebsite(
        { id: website._id, body: formData },
        {
          onSuccess: () => {
            close()
            showSuccessMessage()
          },
          onError: () => {
            showErrorMessage()
          },
        }
      )
    } else {
      const formData: NSocietiesWebsites.NPatch.IRequestBody = {
        brokerQuestion: {
          ...website.brokerQuestion,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          sections: website.brokerQuestion?.sections?.map((_section) => {
            if (_section._id === sectionId) {
              return {
                ..._section,
                questions: [
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  ..._section.questions,
                  {
                    ...data,
                    order: _section.questions ? _section.questions.length : 0,
                  },
                ],
              }
            }
            return _section
          }),
        },
      }
      mutatePatchWebsite(
        { id: website._id, body: formData },
        {
          onSuccess: () => {
            close()
            showSuccessMessage()
          },
          onError: () => {
            showErrorMessage()
          },
        }
      )
    }
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const loading = isUpdatingWebsite

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate(
                updateMode
                  ? 'createUpdateQuestionsAndAnswers.editQuestionTitle'
                  : 'createUpdateQuestionsAndAnswers.addNewQuestionTitle'
              )}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              <CreateUpdateSocietyWebsiteFAQItemForm
                onError={onError}
                onSubmit={onSubmit}
                website={website}
                onClose={close}
                loading={loading}
                sectionId={sectionId}
                questionId={questionId}
              />
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
